import React, { useState, useEffect, useCallback } from 'react';
import { Settings, FileText, ChevronRight, ChevronDown, BookOpen, Plus, X, ClipboardList, History } from 'lucide-react';
import ChecklistQuestionario from './ChecklistQuestionario';
import ChecklistVersoes from './ChecklistVersoes';
import ChecklistHistorico from './ChecklistHistorico';
import useEhCompacto from '../../../utils/useEhCompacto';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =====================================================================
// Sub-hub "Auditoria Financeira e de Estoques" (Módulo Operacional).
//
// DEM-00011 (Fase 4, T21): morava no módulo Auditoria (arquivo
// `AuditoriaForms.js`, sub-hub "Formulários") — mudou de TELA, não de tabela
// nem de rota de API: `/api/auditoria/checklist` e `/api/auditoria/versoes`
// continuam os mesmos (ver CLAUDE.md › migrations e docs/modulos/auditoria.md).
//
// Reorganizado em 2026-09-02 no padrão do sub-hub Avaliação BSV
// (operacional/bsv/OperacionalBsv.js): a tela inicial é a CONSULTA
// (ChecklistHistorico — "Auditorias Gravadas"), e as ações vivem no cabeçalho
// dela:
//   - "Nova Auditoria"     → pop-up com os checklists publicados + o manual.
//   - "Gerenciar versões"  → só Admin/Auditoria.
//
// DUAS ABAS desde 2026-09-23 (pedido da Julia, mesmo padrão de
// OperacionalAval360.js × Histórico BSV): "Auditoria Financeira e de
// Estoques" (método CONTAGEM — onde se preenche) e "Histórico" (método PESO
// — as 28 auditorias antigas, só leitura). Diferença do par 360×BSV: lá são
// DUAS TABELAS (dtbAval360Avaliacao × dtbAvaliacaoBsv); aqui é a MESMA tabela
// (dtbAuditoriaChecklist) filtrada por método — por isso as duas abas
// reusam o mesmo `ChecklistHistorico`, só com a prop `metodo` diferente,
// em vez de dois componentes.
//
// O QUE MUDOU E POR QUÊ (2026-09-02): antes a tela inicial era um MENU de
// cards com os checklists publicados, e o histórico era um botão secundário
// ("Auditorias gravadas"). Ou seja, a primeira coisa que se via era a lista do
// que dá para PREENCHER, e o resultado do trabalho já feito ficava escondido
// um clique adiante. Invertido: o dia a dia de quem audita é CONSULTAR
// (comparar lojas, baixar o PDF, olhar a classificação); preencher é
// episódico e ganhou um botão de destaque.
//
// A navegação para fora do sub-hub é a barra de abas do módulo Operacional —
// por isso a consulta não tem botão "Voltar" próprio.
//
// A paleta é a ARDÓSIA do Operacional (#475569) — a tela ASSUME a cor do
// módulo que passou a morar (CLAUDE.md › "Tela dentro de um módulo usa a COR
// do módulo"). Era laranja (#d8711d) quando vivia no módulo Auditoria.
//
// ⚠️ NÃO existe banner de "auditoria em andamento" aqui, ao contrário do BSV: o
//    checklist não tem autosave — o preenchimento vive só na memória da tela
//    até o "Finalizar". Se um dia ganhar rascunho, o banner do
//    OperacionalBsv.js é o molde pronto.
// =====================================================================

const API_BASE = '/api/auditoria/checklist';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const OperacionalChecklist = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // ⚠️ `perfilNoModulo(..., 'operacional')` e NUNCA o perfil cru: sem isso a
  //    `Diretoria Op` chega como 'Diretoria Op', não casa com nenhuma lista e
  //    perde os botões — some a ação sem erro nenhum na tela.
  const perfil = perfilNoModulo(user?.perfil, 'operacional') || '';

  // Espelho das listas do backend. Divergir aqui não dá erro em lugar nenhum:
  // o botão some para quem podia usar, ou aparece e a chamada volta 403.
  //   preencher  → server-checklist.js         › PERFIS_PERMITIDOS
  //   versões    → server-checklist-versoes.js › PERFIS_GESTAO
  const podePreencher = ['Auditoria', 'Admin', 'Diretoria', 'Gerente Adm', 'Operacional'].includes(perfil);
  const podeGerenciarVersoes = ['Admin', 'Auditoria', 'Operacional'].includes(perfil);

  // Aba do card: 'atual' (Contagem — nova versão, é onde se preenche) |
  // 'historico' (Peso — as 28 auditorias antigas, só leitura). Mesmo padrão
  // da Avaliação 360 × Histórico BSV (2026-09-23, pedido da Julia).
  const [aba, setAba] = useState('atual');
  const [tela, setTela] = useState('lista');
  const [versaoSelecionada, setVersaoSelecionada] = useState(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [versoes, setVersoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Os checklists publicados só são buscados quando o pop-up abre — quem não
  // pode preencher não vê o botão e, portanto, nunca dispara esta chamada.
  const carregarVersoes = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/versoes-publicadas`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os checklists publicados.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirNovo = () => { setModalNovo(true); carregarVersoes(); };

  const escolherVersao = (v) => {
    setVersaoSelecionada(v); setModalNovo(false); setTela('preencher');
  };

  if (tela === 'preencher' && versaoSelecionada) {
    return (
      <ChecklistQuestionario
        idVersao={versaoSelecionada.ID}
        onVoltar={() => { setTela('lista'); setVersaoSelecionada(null); }}
      />
    );
  }

  if (tela === 'versoes') return <ChecklistVersoes onVoltar={() => setTela('lista')} />;

  // Botões de ação do cabeçalho da tela inicial (Auditorias Gravadas).
  const acoes = (
    <>
      {podePreencher && (
        <button onClick={abrirNovo} style={btnPrimario}>
          <Plus size={16} /> Nova Auditoria
        </button>
      )}
      {podeGerenciarVersoes && (
        <button onClick={() => setTela('versoes')} style={btnSecundario}>
          <Settings size={16} /> Gerenciar versões
        </button>
      )}
    </>
  );

  return (
    <>
      {/* Abas do card: a Auditoria atual (Contagem) e o Histórico (Peso, só leitura). */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
        <button onClick={() => setAba('atual')} style={estiloAba(aba === 'atual')}>
          <ClipboardList size={15} /> Auditoria Financeira e de Estoques
        </button>
        <button onClick={() => setAba('historico')} style={estiloAba(aba === 'historico')}>
          <History size={15} /> Histórico
        </button>
      </div>

      {aba === 'historico' ? (
        <>
          {/* Aviso: instrumento mudou de método; a série antiga usa a régua/vocabulário ANTIGOS. */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
            border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 12, padding: '12px 14px', marginBottom: 16,
          }}>
            <History size={18} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, color: '#92400e' }}>
              <strong>Estas auditorias usaram o método Peso (versões v2 e v3).</strong> Este histórico é{' '}
              <strong>somente leitura</strong> — a régua e o vocabulário aqui
              (Excelente/Satisfatório/Atenção/Crítico) são os <strong>ANTIGOS</strong>; a versão atual usa a
              régua nova por contagem (Verde/Amarelo/Laranja/Vermelho). As auditorias e seus PDFs seguem acessíveis.
            </div>
          </div>
          <ChecklistHistorico metodo="PESO" titulo="Histórico — método Peso" />
        </>
      ) : (
        <ChecklistHistorico metodo="CONTAGEM" acoesExtras={acoes} />
      )}

      {modalNovo && (
        <ModalNovaAuditoria
          versoes={versoes} loading={loading} erro={erro}
          podeGerenciarVersoes={podeGerenciarVersoes}
          onEscolher={escolherVersao} onFechar={() => setModalNovo(false)}
        />
      )}
    </>
  );
};

// Botão de aba do card (Auditoria atual × Histórico). A ativa ganha a cor do
// módulo e uma borda inferior; a inativa fica apagada. Mesmo estilo de
// OperacionalAval360.js (Avaliação 360 × Histórico BSV).
const estiloAba = (ativa) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
  background: 'transparent', border: 'none', borderBottom: `3px solid ${ativa ? '#475569' : 'transparent'}`,
  color: ativa ? '#0f172a' : '#94a3b8', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: -1,
});

// ---------------------------------------------------------------------
// Pop-up "Nova Auditoria": os checklists publicados (clicar = iniciar o
// preenchimento) + o manual "como funciona", recolhível.
//
// Sem filtro na lista: cada linha JÁ É uma versão publicada, e um filtro de
// versão sobre uma lista de versões não separaria nada (mesma decisão do BSV).
// ---------------------------------------------------------------------
const ModalNovaAuditoria = ({ versoes, loading, erro, podeGerenciarVersoes, onEscolher, onFechar }) => {
  const compacto = useEhCompacto();
  const [manualAberto, setManualAberto] = useState(false);

  // Esc fecha o modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: compacto ? 10 : 24, overflowY: 'auto',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720,
          marginTop: compacto ? 8 : 24, boxShadow: '0 20px 50px rgba(15,23,42,.25)', overflow: 'hidden',
        }}>
        {/* Cabeçalho do modal */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: compacto ? '14px 16px' : '18px 22px', borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{ padding: 10, background: '#f1f5f9', borderRadius: 10 }}><Plus size={20} color="#475569" /></div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Nova Auditoria</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Escolha o checklist publicado para iniciar a visita.</p>
          </div>
          <button onClick={onFechar} title="Fechar" style={{
            display: 'inline-flex', padding: 8, background: '#f1f5f9', color: '#475569',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: compacto ? 14 : 22 }}>
          {/* Manual: como funciona / como criar um novo checklist — recolhível */}
          <div style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            <button onClick={() => setManualAberto((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#334155', fontSize: 14,
            }}>
              <BookOpen size={18} /> Como funciona / criar um novo checklist
              <ChevronDown size={18} style={{ marginLeft: 'auto', transform: manualAberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {manualAberto && (
              <div style={{ padding: '0 18px 16px', fontSize: 13, color: '#334155', lineHeight: 1.65 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    <strong>Monte o checklist</strong> — em <strong>Gerenciar versões &rarr; Nova versão</strong>: dê um nome à versão
                    (ex.: &ldquo;v1 &mdash; Auditoria Financeiro 2026&rdquo;), escolha o <strong>método</strong> (Peso ou Contagem),
                    crie as <strong>áreas</strong> e, dentro de cada uma, as <strong>perguntas</strong>. Marque quais perguntas
                    <strong> permitem N/A</strong>. Por fim, <strong>publique</strong> a versão.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Faça a auditoria</strong> — na lista abaixo, clique no checklist publicado: informe
                    <strong> loja, data, auditor</strong> e os gerentes, e responda cada pergunta como <strong>Sim / Não</strong>
                    (ou <strong>N/A</strong>, onde a versão permitir). Na pergunta marcada <strong>Não</strong> dá para
                    <strong> anexar uma foto</strong> da evidência &mdash; é <strong>opcional</strong>. A nota aparece ao vivo,
                    por área e no total.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    ⚠️ <strong>Não saia da tela no meio</strong> &mdash; o checklist <strong>não</strong> tem salvamento
                    automático. O preenchimento só existe no navegador até você <strong>Finalizar</strong>; fechar a aba antes disso
                    perde tudo.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Finalize e consulte</strong> — ao finalizar, o sistema calcula a nota e gera o <strong>PDF</strong>.
                    A auditoria passa a aparecer aqui em <strong>Auditorias Gravadas</strong>, onde dá para abrir o detalhe por área,
                    baixar o <strong>PDF</strong> e exportar a lista em <strong>Excel</strong>. ⚠️ <strong>As fotos aparecem só no
                    PDF</strong> &mdash; não há galeria na tela.
                  </li>
                </ol>
                {/* DEM-00011 (T15): DOIS métodos convivem, escolhidos pela versão —
                    nunca por data. As auditorias antigas (método Peso) ficam congeladas
                    na régua antiga; o texto abaixo explica as duas, sem esconder qual
                    vale para cada checklist (isso a lista de versões já mostra). */}
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8 }}>
                  <strong style={{ color: '#334155' }}>Como o resultado é decidido</strong> — depende do <strong>método</strong> da
                  versão escolhida (aparece ao lado do nome dela, na lista abaixo):<br />
                  <strong>Peso</strong> (checklists antigos): pergunta que atende soma o <strong>peso</strong> dela; % = soma
                  &divide; soma dos pesos <strong>aplicáveis</strong>. Faixas: &ge;90% <strong>Excelente</strong> ·
                  &ge;80% <strong>Satisfatório</strong> · &ge;60% <strong>Atenção</strong> · &lt;60% <strong>Crítico</strong>.<br />
                  <strong>Contagem</strong> (checklists novos): cada pergunta vale o <strong>mesmo</strong> (o peso é ignorado);
                  % = Sim &divide; (Sim+Não). Faixas: &ge;90% <strong>Verde</strong> · &ge;80% <strong>Amarelo</strong> ·
                  &ge;70% <strong>Laranja</strong> · &lt;70% <strong>Vermelho</strong>.<br />
                  Nos dois métodos, pergunta marcada <strong>N/A sai do denominador</strong> — não conta nem a favor nem contra.
                </div>
              </div>
            )}
          </div>

          {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

          {loading ? <p style={{ color: '#64748b' }}>Carregando…</p> : versoes.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              Nenhum checklist publicado.{podeGerenciarVersoes && ' Use "Gerenciar versões" para publicar um.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {versoes.map((v) => (
                <div key={v.ID}
                  onClick={() => onEscolher(v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', transition: 'border-color .15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                  <div style={{ padding: 12, background: '#f1f5f9', borderRadius: 10 }}><FileText size={22} color="#475569" /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</div>
                    {v.DESCRICAO && <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>{v.DESCRICAO}</div>}
                    {/* DEM-00011 (T15): "Pontuação máxima" não faz sentido numa versão
                        CONTAGEM (o peso da pergunta é ignorado) — mostra o método em vez. */}
                    <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                      {v.METODO === 'CONTAGEM' ? 'Contagem pura (sem peso)' : `Pontuação máxima: ${v.PESO_MAXIMO_TOTAL}`}
                    </div>
                  </div>
                  <ChevronRight size={20} color="#cbd5e1" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Botão primário "Nova Auditoria" — cheio, na ardósia do Operacional, para ser
// a ação de destaque do cabeçalho.
const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#475569', color: '#fff', border: '1px solid #475569',
  borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default OperacionalChecklist;
